import React, { useState } from 'react';
import { View, Text, StyleSheet, Pressable, ScrollView } from 'react-native';
import { AntDesign } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';

interface UserProfile {
  name?: string;
  surname?: string;
  avatar?: any;
}

interface PendingUser {
  _id: string;
  email?: string;
  profile_information?: UserProfile;
  status: 'pending';
  isPending: true;
  invitedAt?: string;
}

interface PendingUsersPanelProps {
  onRemovePendingUser?: (userId: string) => void;
  onResendInvite?: (user: PendingUser) => void;
}

const PendingUsersPanel: React.FC<PendingUsersPanelProps> = ({
  onRemovePendingUser,
  onResendInvite
}) => {
  const theme = themeSelector();
  const [isExpanded, setIsExpanded] = useState(false);

  // Get pending users from Redux
  const pendingUsers = useSelector((state: any) => {
    const users = state.userHierarchy?.pendingUsers;
    return Array.isArray(users) ? users : [];
  });

  if (pendingUsers.length === 0) {
    return null;
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const formatInviteTime = (invitedAt?: string) => {
    if (!invitedAt) return 'Recently';
    const date = new Date(invitedAt);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <View style={[
      styles.container,
      {
        backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 30, 30, 0.95)',
        borderColor: theme ? '#e5e7eb' : '#374151',
      }
    ]}>
      {/* Header */}
      <Pressable
        style={[styles.header, { borderBottomColor: theme ? '#e5e7eb' : '#374151' }]}
        onPress={toggleExpanded}
      >
        <View style={styles.headerContent}>
          <View style={[
            styles.badge,
            { backgroundColor: '#f59e0b' }
          ]}>
            <Text style={styles.badgeText}>{pendingUsers.length}</Text>
          </View>
          <Text style={[
            styles.headerText,
            { color: theme ? '#1f2937' : '#f9fafb' }
          ]}>
            Pending Invites
          </Text>
        </View>
        <AntDesign
          name={isExpanded ? 'up' : 'down'}
          size={16}
          color={theme ? '#6b7280' : '#9ca3af'}
        />
      </Pressable>

      {/* Expanded Content */}
      {isExpanded && (
        <View style={styles.content}>
          <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
            {pendingUsers.map((user: PendingUser) => {
              const userName = user.profile_information?.name || 'Unknown';
              const userSurname = user.profile_information?.surname || '';
              const userEmail = user.email || 'No email';
              const userAvatar = user.profile_information?.avatar;

              return (
                <View
                  key={user._id}
                  style={[
                    styles.userItem,
                    { borderColor: theme ? '#e5e7eb' : '#374151' }
                  ]}
                >
                  {/* User Info */}
                  <View style={styles.userInfo}>
                    <AvatarHandler
                      avatar={userAvatar?.img}
                      avatarImageStyle={{
                        width: 40,
                        height: 40,
                        borderRadius: 20,
                        resizeMode: 'cover'
                      }}
                    />
                    <View style={styles.userDetails}>
                      <Text style={[
                        styles.userName,
                        { color: theme ? '#1f2937' : '#f9fafb' }
                      ]}>
                        {userName} {userSurname}
                      </Text>
                      <Text style={[
                        styles.userEmail,
                        { color: theme ? '#6b7280' : '#9ca3af' }
                      ]}>
                        {userEmail}
                      </Text>
                      <Text style={[
                        styles.inviteTime,
                        { color: theme ? '#9ca3af' : '#6b7280' }
                      ]}>
                        Invited {formatInviteTime(user.invitedAt)}
                      </Text>
                    </View>
                  </View>

                  {/* Actions */}
                  <View style={styles.actions}>
                    {onResendInvite && (
                      <Pressable
                        style={[styles.actionButton, { backgroundColor: '#3b82f6' }]}
                        onPress={() => onResendInvite(user)}
                      >
                        <AntDesign name="reload1" size={14} color="white" />
                      </Pressable>
                    )}
                    {onRemovePendingUser && (
                      <Pressable
                        style={[styles.actionButton, { backgroundColor: '#ef4444' }]}
                        onPress={() => onRemovePendingUser(user._id)}
                      >
                        <AntDesign name="close" size={14} color="white" />
                      </Pressable>
                    )}
                  </View>
                </View>
              );
            })}
          </ScrollView>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 20,
    right: 20,
    width: 320,
    maxHeight: 400,
    borderRadius: 12,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 1000,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  badge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
  },
  badgeText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
  },
  headerText: {
    fontSize: 16,
    fontWeight: '600',
  },
  content: {
    maxHeight: 300,
  },
  scrollView: {
    flex: 1,
  },
  userItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderBottomWidth: 1,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  userDetails: {
    marginLeft: 12,
    flex: 1,
  },
  userName: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  userEmail: {
    fontSize: 12,
    marginBottom: 2,
  },
  inviteTime: {
    fontSize: 10,
    fontStyle: 'italic',
  },
  actions: {
    flexDirection: 'row',
    gap: 8,
  },
  actionButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default PendingUsersPanel;
